import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {mate} from 'genetix/Flip.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import SynthEnvelope from '../SynthEnvelope.js';

class MasterPitchEnvelope {
    constructor(sequencerEngine, mixSession, context) {
        this.sequencerEngine = sequencerEngine;
        this.store = sequencerEngine.store;
        this.mixSession = mixSession;
        this.context = context.getCopy().forSubDevice(this);
        this.parameters = [];
        this.envelope = new SynthEnvelope(
            this.sequencerEngine, this.mixSession, this.context,
            undefined, -2000, 2000);
        this.setupParameters();
    }

    setupParameters() {
        this.attackParameter = new AbstractParameter(
            'Attack',
            this, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.attack = b;
            },
            {
                min: 0,
                max: 10000,
                unit: 'ms',
                defaultValue: 1000
            });

        this.registerParameter(this.attackParameter);

        this.decayParameter = new AbstractParameter(
            'Decay',
            this, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.decay = b;
            },
            {
                min: 0,
                max: 4000,
                unit: 'ms',
                defaultValue: 1000
            });

        this.registerParameter(this.decayParameter);

        this.sustainParameter = new AbstractParameter(
            'Sustain',
            this, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.sustain = b;
            }, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0.5
            });
            
        this.registerParameter(this.sustainParameter);

        this.releaseParameter = new AbstractParameter(
            'Release',
            this.envelope, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.release = b;
            }, {
                min: 0,
                max: 5000,
                unit: 'ms',
                defaultValue: 1000
            });

        this.registerParameter(this.releaseParameter);

        this.levelParameter = new AbstractParameter(
            'Level',
            this, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.level = b;
            }, {
                min: -1,
                max: 1,
                unit: '',
                defaultValue: 0
            });

        this.registerParameter(this.levelParameter);

        this.attackCurveParameter = new AbstractParameter(
            'Attack Curve',
            this.envelope, (a, b) => a.attackCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: 'linear',
                automatable: false
            });

        this.registerParameter(this.attackCurveParameter);

        this.decayCurveParameter = new AbstractParameter(
            'Decay Curve',
            this.envelope, (a, b) => a.decayCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                automatable: false,
                defaultValue: 'linear'
            });

        this.registerParameter(this.decayCurveParameter);

        this.releaseCurveParameter = new AbstractParameter(
            'Release Curve',
            this.envelope, (a, b) => a.releaseCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                automatable: false,
                defaultValue: 'exponential'
            });

        this.registerParameter(this.releaseCurveParameter);
    }

    registerParameter(parameter) {
        parameter.setStore(this.store);
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    getJson(isPreset) {
        let json = {
            level: this.level,
            attack: this.attack,
            decay: this.decay,
            sustain: this.sustain,
            release: this.release,
            attackCurve: this.attackCurve,
            decayCurve: this.decayCurve,
            releaseCurve: this.releaseCurve,
        };

        json = saveParameterToJson(json, 'attackParameter', this.attackParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'decayParameter', this.decayParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'sustainParameter', this.sustainParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'releaseParameter', this.releaseParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'levelParameter', this.levelParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'attackCurveParameter', this.attackCurveParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'decayCurveParameter', this.decayCurveParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'releaseCurveParameter', this.releaseCurveParameter, undefined, isPreset);
        return json;
    }

    fromJson(json) {
        let bulk = {};
        bulkUpdate(this.attackParameter, json.attack, bulk);
        bulkUpdate(this.decayParameter, json.decay, bulk);
        bulkUpdate(this.sustainParameter, json.sustain, bulk);
        bulkUpdate(this.releaseParameter, json.release, bulk);
        bulkUpdate(this.levelParameter, json.level, bulk);

        if (json.attackCurve) {
            bulkUpdate(this.attackCurveParameter, json.attackCurve, bulk);
        }
        if (json.decayCurve) {
            bulkUpdate(this.decayCurveParameter, json.decayCurve, bulk);
        }
        if (json.releaseCurve) {
            bulkUpdate(this.releaseCurveParameter, json.releaseCurve, bulk);
        }

        bulk = {
            ... bulk,
            ... loadParameterFromJson(json, 'attackParameter', this.attackParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'decayParameter', this.decayParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'sustainParameter', this.sustainParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'releaseParameter', this.releaseParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'levelParameter', this.levelParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'attackCurveParameter', this.attackCurveParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'decayCurveParameter', this.decayCurveParameter, this.sequencerEngine),
            ... loadParameterFromJson(json, 'releaseCurveParameter', this.releaseCurveParameter, this.sequencerEngine)
        };

        return bulk;
    }

    getCopy(context) {
        let pitchEnvelope = new MasterPitchEnvelope(this.sequencerEngine, this.mixSession, context);
        pitchEnvelope.fromJson(this.getJson());
        return pitchEnvelope;
    }

    get attack() {
        return this.attackParameter.get();
    }

    set attack(x) {
        this.attackParameter.set(x);
    }

    get decay() {
        return this.decayParameter.get();
    }

    set decay(x) {
        this.decayParameter.set(x);
    }

    get sustain() {
        return this.sustainParameter.get();
    }

    set sustain(x) {
        this.sustainParameter.set(x);
    }

    get release() {
        return this.releaseParameter.get();
    }

    set release(x) {
        this.releaseParameter.set(x);
    }

    get level() {
        return this.levelParameter.get();
    }

    set level(x) {
        this.levelParameter.set(x);
    }

    get attackCurve() {
        return this.attackCurveParameter.get();
    }

    set attackCurve(x) {
        this.attackCurveParameter.set(x);
    }

    get decayCurve() {
        return this.decayCurveParameter.get();
    }

    set decayCurve(x) {
        this.decayCurveParameter.set(x);
    }

    get releaseCurve() {
        return this.releaseCurveParameter.get();
    }

    set releaseCurve(x) {
        this.releaseCurveParameter.set(x);
    }

    refresh() {
        this.attackParameter.callSetter();
        this.decayParameter.callSetter();
        this.sustainParameter.callSetter();
        this.releaseParameter.callSetter();
        this.levelParameter.callSetter();
    }

    mateJson(partner, probability) {
        this.mateParameter('attack', partner, this.attackParameter, probability);
        this.mateParameter('decay', partner, this.decayParameter, probability);
        this.mateParameter('sustain', partner, this.sustainParameter, probability);
        this.mateParameter('release', partner, this.releaseParameter, probability);
    }

    mateParameter(name, data, param1, probability) {
        mate(this.sequencerEngine, name, data, param1, probability);
    }

    dispose() {
        this.envelope.dispose();
        this.envelope = undefined;
    }
}

export default MasterPitchEnvelope;
